import React, { Fragment, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ScrollableModal } from 'components/organisms/modals/scrollable-modal'
import { Button } from 'components/atoms/button'
import type { TicketSummary, TicketSummaryRow, TicketZoneSummaryRow } from 'graphql/generated/scheme'

interface CheckedInStatisticsModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	ticketSummary?: TicketSummary
	loading: boolean
	lastUpdated: Date
	onRefresh: () => void
}

const getCheckInRate = (checkedIn: number, total: number) => {
	if (total === 0) return 0
	return Math.round((checkedIn / total) * 100)
}

const formatLastUpdated = (date: Date) =>
	date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

// dailyScans[].date is a LocalDate string (YYYY-MM-DD) already in the event's timezone.
// Parse the parts directly so the label is not shifted by the viewer's timezone.
const formatScanDate = (date: string) => {
	const [year, month, day] = date.split('-').map(Number)
	if (!year || !month || !day) return date
	return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		timeZone: 'UTC',
	})
}

export const CheckedInStatisticsModal: React.FC<CheckedInStatisticsModalProps> = ({
	open,
	setOpen,
	ticketSummary,
	loading,
	lastUpdated,
	onRefresh,
}) => {
	return (
		<ScrollableModal
			open={open}
			onClose={() => setOpen(false)}
			ariaLabel="Checked In"
			title="Checked In"
			className="sm:!max-w-xl"
		>
			{/* Event name */}
			{ticketSummary?.event?.displayName && (
				<div className="mb-6 text-center">
					<h2 className="text-xl font-bold mb-1">{ticketSummary.event.displayName}</h2>
					<p className="text-sm text-secondary-text">Event Check-in Statistics</p>
				</div>
			)}

			{/* Last updated + refresh */}
			<div className="flex items-center justify-between mb-6">
				<span className="text-sm text-secondary-text">Last updated: {formatLastUpdated(lastUpdated)}</span>
				<Button onClick={onRefresh} disabled={loading} variant="secondary" className="flex items-center gap-2">
					Refresh
				</Button>
			</div>

			{loading ? (
				<div className="flex justify-center items-center py-12">
					<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="text-secondary-text" />
				</div>
			) : (
				<>
					{/* Summary stat cards */}
					<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
						{[
							{ label: 'Total Issued Tickets', value: ticketSummary?.totalIssuedTickets ?? 0 },
							{ label: 'Total Checked In', value: ticketSummary?.totalCheckedInTickets ?? 0 },
							{ label: 'Total Scans', value: ticketSummary?.totalScans ?? 0 },
							{
								label: 'Check-in Rate',
								value: `${getCheckInRate(ticketSummary?.totalCheckedInTickets ?? 0, ticketSummary?.totalIssuedTickets ?? 0)}%`,
								highlight: true,
							},
						].map(card => (
							<div
								key={card.label}
								className="bg-primary-surface/[0.4] backdrop-blur-lg border border-translucent-plus-border rounded-xl p-4"
							>
								<div className="text-[11px] text-secondary-text uppercase tracking-wider font-semibold mb-1.5">
									{card.label}
								</div>
								<div
									className={
										card.highlight ? 'text-2xl font-bold text-success' : 'text-2xl font-bold'
									}
								>
									{card.value}
								</div>
							</div>
						))}
					</div>

					{/* Ticket & zone breakdown */}
					<div>
						<div className="text-[11px] text-secondary-text uppercase tracking-wider font-semibold mb-4">
							Ticket &amp; Zone Breakdown
						</div>
						<div className="space-y-3">
							{ticketSummary?.ticketSummaries && ticketSummary.ticketSummaries.length > 0 ? (
								ticketSummary.ticketSummaries.map((ticket: TicketSummaryRow, ticketIndex) => {
									const rate = getCheckInRate(ticket.totalCheckedInTickets, ticket.totalIssuedTickets)
									const scannedZones =
										ticket.zoneSummaries?.filter(z => z.totalCheckedInTickets > 0) ?? []
									return (
										<div
											key={ticket.eventTicket?.eventTicketId ?? ticketIndex}
											className="bg-primary-surface/[0.4] backdrop-blur-lg border border-translucent-plus-border rounded-xl p-4"
										>
											{/* Ticket header */}
											<div className="flex items-center justify-between gap-4 mb-3">
												<div className="flex items-center gap-2 min-w-0">
													<span className="font-semibold truncate">
														{ticket.eventTicket?.ticketType ?? 'Unknown Ticket'}
													</span>
													{ticket.eventTicket?.isPrivate && (
														<span className="flex-shrink-0 px-2 py-0.5 bg-accent-text/20 border border-accent-text/30 rounded-full text-[11px] text-accent-text">
															Private
														</span>
													)}
												</div>
												<div className="text-right flex-shrink-0">
													<div className="font-semibold">
														{ticket.totalCheckedInTickets}{' '}
														<span className="text-secondary-text font-normal">
															/ {ticket.totalIssuedTickets}
														</span>
													</div>
													<div className="text-xs text-secondary-text">
														{rate}% checked in
													</div>
													<div className="text-xs text-secondary-text">
														{ticket.totalScans} scans
													</div>
												</div>
											</div>

											{/* Progress bar */}
											<div className="h-1 bg-white/[0.08] rounded-full mb-3 overflow-hidden">
												<div
													className="h-full rounded-full bg-success transition-all duration-500"
													style={{ width: `${Math.min(rate, 100)}%` }}
												/>
											</div>

											{/* Zone rows */}
											{scannedZones.length > 0 ? (
												<table className="w-full">
													<thead>
														<tr className="text-[11px] text-secondary-text uppercase tracking-wider">
															<th className="text-left font-semibold py-1">Zone</th>
															<th className="text-right font-semibold py-1">Scans</th>
														</tr>
													</thead>
													<tbody>
														{scannedZones.map((zone: TicketZoneSummaryRow, zoneIndex) => {
															const dailyScans =
																zone.dailyScans?.filter(d => d.totalScans > 0) ?? []
															return (
																<Fragment key={zone.eventTicketZoneId ?? zoneIndex}>
																	<tr className="border-t border-translucent-plus-border/50">
																		<td className="py-1.5 text-sm text-secondary-text">
																			{zone.zoneName ?? `Zone ${zoneIndex + 1}`}
																		</td>
																		<td className="py-1.5 text-sm text-right font-medium">
																			{zone.totalScans}
																			{zone.totalCheckedInTickets !== zone.totalScans && (
																				<span className="text-xs text-secondary-text font-normal">
																					{' '}
																					· {zone.totalCheckedInTickets}{' '}
																					{zone.totalCheckedInTickets === 1
																						? 'ticket'
																						: 'tickets'}
																				</span>
																			)}
																		</td>
																	</tr>
																	{dailyScans.length > 0 && (
																		<tr>
																			<td colSpan={2} className="pb-2 pl-2">
																				<div className="flex flex-wrap gap-1.5">
																					{dailyScans.map(day => (
																						<span
																							key={String(day.date)}
																							className="px-2 py-0.5 rounded-full bg-white/[0.06] border border-translucent-plus-border/50 text-[11px] text-secondary-text"
																						>
																							{formatScanDate(day.date)}:{' '}
																							<span className="text-primary-text font-medium">
																								{day.totalScans}
																							</span>
																						</span>
																					))}
																				</div>
																			</td>
																		</tr>
																	)}
																</Fragment>
															)
														})}
													</tbody>
												</table>
											) : (
												<p className="text-xs text-secondary-text">
													No zone scan data available
												</p>
											)}
										</div>
									)
								})
							) : (
								<p className="text-center py-8 text-secondary-text">No ticket data available</p>
							)}
						</div>
					</div>
				</>
			)}
		</ScrollableModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface CheckedInStatisticsModalManagedProps {
	ticketSummary?: TicketSummary
	loading: boolean
	lastUpdated: Date
	onRefresh: () => void
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'checked-in-statistics': CheckedInStatisticsModalManagedProps
	}
}

export function ManagedCheckedInStatisticsModal({
	onClose,
	...rest
}: CheckedInStatisticsModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<CheckedInStatisticsModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
